import React, { useState } from 'react';
import { X, Buildings } from '@phosphor-icons/react';
import { api } from '../lib/api';

const TIER_OPTIONS = ['Big 6', 'Monoline', 'Specialty', 'Credit Union', 'Private', 'Alt-A'];

/**
 * Manual "Add a lender" modal — for lenders Mat has access to but doesn't
 * receive email rate sheets from. Once created, Mat lands on the new
 * lender's profile and can upload PDF rate sheets to keep rates current.
 */
export default function CreateLenderModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [shortCode, setShortCode] = useState('');
  const [tier, setTier] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.post('/lenders', {
        name: name.trim(),
        short_code: shortCode.trim() || null,
        tier_label: tier || null,
      });
      onCreated?.(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Create failed');
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-white/80 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="create-lender-modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <form
        onSubmit={submit}
        className="bg-zinc-50 border border-zinc-200 w-full max-w-md rounded-md"
      >
        <header className="flex items-start justify-between gap-3 p-5 border-b border-zinc-200">
          <div className="flex items-center gap-2">
            <Buildings size={18} className="text-emerald-600" />
            <div>
              <div className="label-overline">Add a lender</div>
              <h2 className="font-heading text-xl font-semibold tracking-tight mt-0.5">New lender record</h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="create-lender-close"
            className="text-zinc-500 hover:text-zinc-950"
          >
            <X size={18} />
          </button>
        </header>

        <div className="p-5 space-y-4">
          <p className="text-xs text-zinc-500 leading-relaxed">
            For lenders you have access to but don't receive email rate sheets from. Once created, you can upload PDF rate sheets from the lender's profile page to keep their rates current.
          </p>

          <Field label="Lender name" required>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Equitable Bank"
              data-testid="create-lender-name"
              required
              autoFocus
              className="w-full bg-white border border-zinc-200 px-3 py-2 text-sm rounded-sm"
            />
          </Field>

          <Field label="Short code (optional)" hint="2-5 letters · auto-generated from name if blank">
            <input
              type="text"
              value={shortCode}
              onChange={(e) => setShortCode(e.target.value.toUpperCase().slice(0, 5))}
              placeholder="EQB"
              maxLength={5}
              data-testid="create-lender-short-code"
              className="w-full bg-white border border-zinc-200 px-3 py-2 text-sm rounded-sm font-data tracking-wider"
            />
          </Field>

          <Field label="Tier (optional)">
            <select
              value={tier}
              onChange={(e) => setTier(e.target.value)}
              data-testid="create-lender-tier"
              className="w-full bg-white border border-zinc-200 px-3 py-2 text-sm rounded-sm"
            >
              <option value="">— Select —</option>
              {TIER_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>

          {error && (
            <div className="text-[12px] text-red-600 border border-red-500/30 bg-red-500/5 px-3 py-2 rounded-sm" data-testid="create-lender-error">
              {error}
            </div>
          )}
        </div>

        <footer className="border-t border-zinc-200 p-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-xs text-zinc-600 hover:text-zinc-950 px-3 py-2 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || !name.trim()}
            data-testid="create-lender-submit"
            className="bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-medium px-4 py-2 rounded-sm disabled:opacity-50"
          >
            {busy ? 'Creating…' : 'Create lender'}
          </button>
        </footer>
      </form>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1">
        <span className="label-overline text-[10px]">
          {label}{required && <span className="text-red-600 ml-1">*</span>}
        </span>
        {hint && <span className="text-[10px] text-zinc-500">{hint}</span>}
      </div>
      {children}
    </label>
  );
}
